import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { ArrowBack, Print, Warning } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetPosExchangeByIdQuery } from '../services/exchangeApi';
import ThermalExchangeReceipt80mm from '../components/pos/receipt/ThermalExchangeReceipt80mm';
import { mapExchangeToReceipt } from '../components/pos/receipt/mapExchangeToReceipt';
import { useGetReceiptTemplateQuery } from '../services/receiptTemplateApi';
import { usePrinterContext } from '../context/PrinterContext';

const PrintExchangePage = () => {
  const { orderId, exchangeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { data, isFetching, isError } = useGetPosExchangeByIdQuery(
    { orderId, id: exchangeId },
    { skip: !orderId || !exchangeId },
  );

  const { data: receiptTemplate } = useGetReceiptTemplateQuery();
  const receipt = useMemo(() => mapExchangeToReceipt(data, receiptTemplate), [data, receiptTemplate]);

  const searchParams = new URLSearchParams(location.search || '');
  const shouldAutoPrint =
    location.state?.autoPrint === true || searchParams.get('autoprint') === '1';

  const { isConnected, silentPrintExchange } = usePrinterContext();
  const printerRef = useRef(null);
  printerRef.current = { isConnected, silentPrintExchange };

  useEffect(() => {
    const previousTitle = document.title;
    document.title = receipt ? `Exchange ${receipt.exchangeNumber}` : 'Print Exchange';
    return () => {
      document.title = previousTitle;
    };
  }, [receipt]);

  useEffect(() => {
    if (!receipt || !shouldAutoPrint || receipt.status !== 'completed') return;
    const timer = window.setTimeout(async () => {
      const { isConnected: connected, silentPrintExchange: silentPrint } = printerRef.current;
      if (connected) {
        try {
          await silentPrint(receipt);
          return;
        } catch {
          // companion error — fall through to browser print
        }
      }
      window.print();
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [receipt, shouldAutoPrint]);

  const statusWarning =
    receipt?.status === 'draft'
      ? 'This exchange is not yet complete — receipt is a preview.'
      : receipt?.status === 'cancelled'
      ? 'This exchange was cancelled.'
      : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#E2E8F0', p: { xs: 1.5, sm: 2.5 } }}>
      {/* Screen-only controls — hidden in @print via inline style trick */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2, '@media print': { display: 'none' } }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ textTransform: 'none' }}
        >
          Back
        </Button>
        <Button
          startIcon={<Print />}
          variant="contained"
          onClick={() => window.print()}
          disabled={!receipt || receipt.status !== 'completed'}
          sx={{ textTransform: 'none', bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' } }}
        >
          Print Exchange
        </Button>
      </Stack>

      {statusWarning && (
        <Box
          sx={{
            mb: 2, p: 1.5, borderRadius: '10px', bgcolor: '#FFFBEB', border: '1px solid #FDE68A',
            display: 'flex', alignItems: 'center', gap: 1,
            '@media print': { display: 'none' },
          }}
        >
          <Warning sx={{ fontSize: 18, color: '#D97706' }} />
          <Typography fontSize="0.82rem" color="#92400E" fontWeight={600}>{statusWarning}</Typography>
        </Box>
      )}

      <Paper sx={{ maxWidth: 'fit-content', mx: 'auto', p: { xs: 1, sm: 2 }, borderRadius: '16px' }}>
        {isFetching ? (
          <Stack alignItems="center" spacing={1} sx={{ width: '80mm', py: 8 }}>
            <CircularProgress size={24} />
            <Typography sx={{ fontSize: '0.85rem', color: '#64748B' }}>Loading receipt…</Typography>
          </Stack>
        ) : isError || !receipt ? (
          <Stack alignItems="center" spacing={1.5} sx={{ width: '80mm', py: 8 }}>
            <Typography sx={{ fontSize: '0.85rem', color: '#DC2626', fontWeight: 700 }}>
              Exchange not found.
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B', textAlign: 'center' }}>
              Go back and try printing again.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{ textTransform: 'none' }}
            >
              Close tab
            </Button>
          </Stack>
        ) : (
          <>
            {receipt.status !== 'completed' && (
              <Chip
                label={receipt.status === 'draft' ? 'Draft — Preview only' : 'Cancelled'}
                size="small"
                sx={{
                  mb: 1, width: '80mm', borderRadius: '6px', fontWeight: 700, fontSize: '0.72rem',
                  bgcolor: receipt.status === 'cancelled' ? '#FEF2F2' : '#FFFBEB',
                  color: receipt.status === 'cancelled' ? '#DC2626' : '#B45309',
                }}
              />
            )}
            <ThermalExchangeReceipt80mm receipt={receipt} />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default PrintExchangePage;
